import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AgentBreadcrumbComponent } from 'app/core/components/agent-breadcrumb/agent-breadcrumb.component';
import { MotifModule } from "@ey-xd/motif-wc-angular"; 
import { ProgressStep } from 'app/models/progress-steps';
import { ToastService } from 'app/core/service/toast/toast.service';
import { CustomMotifUploaderComponent } from 'app/core/components/custom-motif-uploader/custom-motif-uploader.component';
import { CustomMotifDownloaderComponent } from 'app/core/components/custom-motif-downloader/custom-motif-downloader.component';
import {
  IconoirArrowRight,
  IconoirCheckCircle,
  IconoirCloudUpload,
  IconoirHome,
  IconoirInfoCircle,
  IconoirNavArrowDown,
  IconoirQuestionMark,
  IconoirSearch,
  IconoirWarningCircle,
  IconoirOpenNewWindow
} from '@ey-xd/motif-icon';
import { DisclaimerComponent } from 'app/core/components/disclaimer/disclaimer.component';
import { Router } from '@angular/router';
import { IxbrlService } from 'app/core/service/ixbrl/ixbrl.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponseReader } from 'app/core/service/api/api-response.service';
import { take } from 'rxjs';
import { ProcessStatus } from 'app/models/process-status';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ApiFile } from 'app/models/api-response';

import { MotifProgressIndicator, MotifProgressIndicatorStep } from "@ey-xd/motif-wc-angular";

@Component({
  selector: 'app-document-formatting',
  standalone: true,
  imports: [
    CommonModule,
    AgentBreadcrumbComponent,
    // IxbrlInstructionsComponent,
    MotifModule,
    CustomMotifUploaderComponent,
    CustomMotifDownloaderComponent,
    DisclaimerComponent,
  ],
  templateUrl: './document-formatting.component.html',
  styleUrl: './document-formatting.component.scss',
})
export class DocumentFormattingComponent implements OnInit {
  //icons
  IconoirOpenNewWindow = IconoirOpenNewWindow;
  IconoirHome = IconoirHome;
  IconoirCheckCircle = IconoirCheckCircle;
  IconoirArrowRight = IconoirArrowRight;
  IconoirCloudUpload=IconoirCloudUpload;
  IconoirInfoCircle=IconoirInfoCircle;
  IconoirNavArrowDown=IconoirNavArrowDown;
  IconoirQuestionMark=IconoirQuestionMark;
  IconoirSearch=IconoirSearch;
  IconoirWarningCircle=IconoirWarningCircle;

  steps: ProgressStep[] = [
    {
      stepNumber: 1,
      title: 'Upload documents',
      status: 'completed',
      disabled: true,
    },
    {
      stepNumber: 2,
      title: 'Formatted documents',
      status: 'default',
      disabled: true,
    },
  ];

  currentStep: number = 1;

  // Step 1 variables to store file and control errors
  fileToFormat: File | any = undefined;
  fileToFormatHasError: boolean | any = false;

  // file uploaded timestamp
  fileUploadedOn: string | undefined = '';

  // To control spinners
  // step one
  runDocFormattingLoading: boolean = false;
  docFormattingCanceled: boolean = false;

  // Progress bar loading message
  loadingMessage: string = '';

  //To retrieve the id from the formatted doc
  formattedDocId: string = '';

  // This is to store info for the formatted output file from step 2
  formattedFile: ApiFile | undefined | any = undefined;

  docFormattingDuration: string = '';

  // Zoom related properties for breakpoint observer
  isZoomed: boolean = false;

  constructor(
    private toastService: ToastService,
    private router: Router,
    private ixbrl: IxbrlService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .subscribe((result) => {
        // result matches returns true if the width is <= 900px
        this.isZoomed = result.matches;
      });
  }

  ngOnInit(): void {
    this.setStepStatus();
  }

  setStepStatus() {
    this.steps.forEach((step) => {
      if (step.stepNumber === this.currentStep) {
        step.status = 'active';
        step.disabled = false;
      }
    });
  }

  /** Stepper control functions  */
  goToNextStep(clearToast: boolean = false) {
    if (clearToast) {
      this.toastService.clearToastWithTag(true);
    }

    let stp: ProgressStep | any = this.steps.find(
      (step) => step.stepNumber === this.currentStep
    );
    if (stp) {
      stp.status = 'completed';
    }
    this.currentStep++;
    stp = this.steps.find((step) => step.stepNumber === this.currentStep);
    stp.status = 'active';
  }

  goToPreviousStep(clearToast: boolean = false) {
    if (clearToast) {
      this.toastService.clearToastWithTag(true);
    }

    if (this.currentStep !== 1) {
      let stp: ProgressStep | any = this.steps.find(
        (step) => step.stepNumber === this.currentStep
      );

      stp.status = 'default';
      this.currentStep--;
      stp = this.steps.find((step) => step.stepNumber === this.currentStep);

      if (stp.status === 'completed') {
        stp.status = 'active';
      }
    }
  }
  /** END Stepper control functions  */

  // Handle file selection functions for Agent 1 file uploaders
  onFileToFormatSelected(event: Event): void {
    this.fileToFormat = event;
  }

  onFileToFormatHasError(event: boolean): void {
    this.fileToFormatHasError = event;
  }

  /** Functions for each step */
  runDocumentFormatting() {
    this.runDocFormattingLoading = true;
    this.setLoadingMessage('uploading');

    this.ixbrl
      .createDocFormatProject(this.fileToFormat)
      .pipe(take(1))
      .subscribe({
        next: (data: ApiResponseReader) => {
          this.formattedDocId = data.id || '';
          this.setLoadingMessage('formatting');
          this.getDataForStepTwo();
        },
        error: (error: HttpErrorResponse) => {
          this.runDocFormattingLoading = false;

          this.toastService.showHttpResponseErrorMessage(
            error,
            'Could not upload the file.'
          );
        },
      });
  }

  private setLoadingMessage(stage: 'uploading' | 'formatting' | 'preview') {
    const messages: Record<string, string> = {
      uploading: 'Uploading document...',
      formatting: 'Formatting and generating document preview...',
      preview: 'Generating preview...',
    };
    this.loadingMessage = messages[stage];
  }

  private getDataForStepTwo() {
    if (this.docFormattingCanceled) {
      this.docFormattingCanceled = false;
      return;
    }

    this.ixbrl
      .getFormatDocProjectById(this.formattedDocId)
      .pipe(take(1))
      .subscribe({
        next: (data: ApiResponseReader) => {
          const status: number = data.status;
          const outputFile: ApiFile | undefined =
            data.outputFile || data.files?.[data.files.length - 1];
          const duration: number | undefined =
            data.getJobByType(40)?.time_ms || 1;
          const createdAt: string | undefined = data.createdAt;

          if (
            status === ProcessStatus.Completed ||
            status === ProcessStatus.WaitingForUser
          ) {
            // 30 is completed, 25 is waiting for user
            this.formattedFile = outputFile;

            this.docFormattingDuration = `Document Formatted in ${
              duration / 1000
            } sec.`;

            this.fileUploadedOn = createdAt;
            this.runDocFormattingLoading = false;
            this.goToNextStep();
          } else if (
            status === ProcessStatus.Created ||
            status === ProcessStatus.Running
          ) {
            // 10 & 20 are running
            setTimeout(() => {
              this.getDataForStepTwo();
            }, 15000);
          } else {
            // 40 or others are Failures
            this.runDocFormattingLoading = false;
            // if the process was canceled and we get here
            // we set it to the base state and avoid the message to appear
            if (this.docFormattingCanceled) {
              this.docFormattingCanceled = false;
            } else {
              this.toastService.showToast(
                'error',
                `Document Formatting process failed. Run the process again. If the error persist, please contact servicedesk@ey.com`,
                {
                  classTheme: '',
                  autoHide: false,
                }
              );
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.runDocFormattingLoading = false;

          if (this.docFormattingCanceled) {
            this.docFormattingCanceled = false;
          } else {
            this.toastService.showHttpResponseErrorMessage(error);
          }
        },
      });
  }

  /**END of Step functions */

  // General functions
  backHome() {
    this.router.navigate(['/ixbrl']);
  }

  scrollUp() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // Adds smooth scrolling effect
    });
  }
}
